// src/utils/analytics.js
//
// GA4 + Google Consent Mode v2 helper — CRA version (process.env.REACT_APP_*).
//
// Load order:
//   1. public/index.html sets Consent Mode defaults to "denied" before React mounts
//   2. initGA() (called from grantAnalyticsConsent, or directly) loads gtag.js —
//      safe even before consent, Consent Mode blocks real data collection until granted
//   3. updateConsent-style functions below are called from CookieConsent.js when
//      the user makes a choice

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let initialized = false;

export function initGA() {
  if (!GA_ID) {
    console.warn("[analytics] REACT_APP_GA_MEASUREMENT_ID is not set — GA4 will not load.");
    return;
  }
  if (initialized) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true, send_page_view: false });
}

// Safety-net call on app boot (public/index.html already sets this earlier,
// this just re-asserts it in case that script is ever removed/edited).
export function setDefaultConsent() {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export function grantAnalyticsConsent() {
  if (!window.gtag) window.gtag = function () { (window.dataLayer = window.dataLayer || []).push(arguments); };
  window.gtag("consent", "update", { analytics_storage: "granted" });
  if (!initialized) initGA();
}

export function revokeAnalyticsConsent() {
  if (!window.gtag) return;
  window.gtag("consent", "update", { analytics_storage: "denied" });
}

// Call on every route change (App.js already does this in AnimatedRoutes).
export function trackPageView(path) {
  if (!window.gtag || !GA_ID) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!window.gtag) return;
  window.gtag("event", name, params);
}

// ============================================================
// Named event helpers — one per tracked interaction (Phase 1)
// All of these are no-ops until the user has granted analytics
// consent (grantAnalyticsConsent() is what loads gtag.js at all).
// ============================================================

// Button / link clicks that matter for conversion or engagement analysis.
// label: human-readable name of the element, e.g. "contact_submit_button"
// location: where on the site it was clicked, e.g. "footer", "hero"
export function trackCTAClick(label, location = "") {
  trackEvent("cta_click", { cta_label: label, cta_location: location });
}

// Fired when the contact form is successfully submitted.
// Treat this as a conversion event in GA4 (mark it as a "key event" in
// GA4 Admin > Events once it starts showing up).
export function trackContactSubmit() {
  trackEvent("contact_submit", { page_path: window.location.pathname });
}

// Fired when the user toggles EN <-> TH.
export function trackLanguageSwitch(newLang) {
  trackEvent("language_switch", { new_language: newLang });
}

// Fired once per threshold per page load (25/50/75/100). See
// useScrollDepthTracking.js for the hook that calls this.
export function trackScrollDepth(percent, path) {
  trackEvent("scroll_depth", { percent_scrolled: percent, page_path: path });
}

// Fired when a named section has been visible in the viewport and the
// user scrolls away from it (or leaves the page). See useSectionAnalytics.js.
export function trackSectionView(sectionName, visibleMs) {
  trackEvent("section_view", {
    section_name: sectionName,
    time_visible_ms: Math.round(visibleMs),
  });
}
